import "core-js/modules/es.symbol.description.js";
const _excluded = ["control", "rules", "layout", "disabled"];
import "core-js/modules/web.dom-collections.iterator.js";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import { InputLabel, TextField, FormLabel, FormControl, FormGroup, FormControlLabel, FormHelperText, Checkbox } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import RadioOptions from "./RadioOptions.js";
import Typeahead from "./Typeahead.js";
import RequiredIndicator from "./RequiredIndicator.js";
import FormErrorMessage from "./FormErrorMessage.js";
import { FIELD_TYPES } from "../constants.js";
import { Box } from '@mui/material';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const AnyField = _ref => {
  let {
      control,
      rules,
      layout,
      disabled
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const renderState = renderType(layout, disabled);
  return /*#__PURE__*/_jsx(Box, _objectSpread(_objectSpread({}, props), {}, {
    children: /*#__PURE__*/_jsx(Controller, {
      control: control,
      rules: rules,
      name: layout.name,
      render: renderState
    })
  }));
};

/**
 * Return the correct renderer for the given type
 * @param {*} layout
 * @returns  {function} the renderer function
 */
const renderType = layout => {
  const {
    id,
    type,
    label,
    options
  } = layout;
  switch (type) {
    case FIELD_TYPES.DATE:
      {
        return dateRenderer(layout);
      }
    case FIELD_TYPES.TEXT:
    case FIELD_TYPES.LONG_TEXT:
    case FIELD_TYPES.INT:
    case FIELD_TYPES.LINK:
    case FIELD_TYPES.FLOAT:
      {
        return textRenderer(layout);
      }
    case FIELD_TYPES.CHOICE:
    case FIELD_TYPES.OBJECT:
      {
        if (layout.multiple && layout.checkbox) {
          return checkboxRenderer(layout);
        }
        return typeaheadRenderer(layout);
      }
    case 'radio':
      {
        const renderRadio = _ref2 => {
          let {
            field: {
              value,
              onChange
            },
            fieldState: {
              error
            }
          } = _ref2;
          // so we need to manually connect a few props here for react hook form
          return /*#__PURE__*/_jsx(RadioOptions, {
            items: options,
            isRequired: true,
            id: id,
            label: label,
            value: value,
            onChange: onChange,
            error: error
          });
        };
        return renderRadio;
      }
    default:
      return /*#__PURE__*/_jsx(TextField, {});
  }
};
AnyField.propTypes = {
  control: PropTypes.object.isRequired,
  rules: PropTypes.object,
  layout: PropTypes.object.isRequired,
  disabled: PropTypes.bool
};

/**
 * This is a custom renderer for the MUI TextField component to work with react-hook-form
 * @param {object} layout Object containing the layout of the field
 * @param {string} layout.id The id of the field
 * @param {string} layout.name The name of the field
 * @param {string} layout.label The label of the field
 * @param {boolean} layout.isMultiLine Whether or not the field is a multi-line text field
 * @param {string} layout.placeholder The placeholder text for the field
 * @param {boolean} layout.required Whether or not the field is required
 * @param {boolean} layout.disabled Whether or not the field is disabled
 * @returns {function} A custom renderer for the MUI TextField component
 */
const textRenderer = _ref3 => {
  let {
    id,
    name,
    label,
    isMultiLine,
    placeholder,
    required,
    disabled
  } = _ref3;
  const TextFieldWrapped = _ref4 => {
    let {
      field: {
        value,
        onChange
      },
      fieldState: {
        error
      }
    } = _ref4;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(InputLabel, {
        htmlFor: id || name,
        error: !!error,
        children: [/*#__PURE__*/_jsx(RequiredIndicator, {
          disabled: disabled,
          isRequired: !!required
        }), label]
      }), /*#__PURE__*/_jsx(TextField, {
        sx: {
          width: '100%'
        },
        disabled: disabled,
        id: id || name,
        error: !!error,
        onChange: onChange,
        value: value,
        multiline: isMultiLine,
        minRows: isMultiLine ? 3 : 1,
        placeholder: placeholder || "Enter ".concat(label),
        variant: "outlined"
      }), /*#__PURE__*/_jsx(FormErrorMessage, {
        error: error
      })]
    });
  };
  TextFieldWrapped.propTypes = {
    field: PropTypes.shape({
      value: PropTypes.any,
      onChange: PropTypes.func
    }),
    fieldState: PropTypes.shape({
      error: PropTypes.any
    })
  };
  return TextFieldWrapped;
};

/**
 * This is a custom renderer for the MUI DatePicker component to work with react-hook-form
 * @param {object} layout Object containing the layout of the field
 * @param {string} layout.id The id of the field
 * @param {string} layout.name The name of the field
 * @param {string} layout.label The label of the field
 * @param {boolean} layout.disabled - whether or not the field is disabled
 * @param {boolean} layout.required - whether or not the field is required *
 * @returns {function} A custom renderer for the MUI DatePicker component
 */
const dateRenderer = _ref5 => {
  let {
    id,
    name,
    label,
    disabled,
    required
  } = _ref5;
  const DateField = _ref6 => {
    let {
      field: {
        value,
        onChange
      },
      fieldState: {
        error
      }
    } = _ref6;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(DatePicker, {
        name: name,
        disabled: disabled,
        id: id,
        value: value,
        onChange: onChange,
        renderInput: params => /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsxs(InputLabel, {
            disabled: disabled,
            htmlFor: id || name,
            error: !!error,
            children: [/*#__PURE__*/_jsx(RequiredIndicator, {
              isRequired: !!required
            }), label]
          }), /*#__PURE__*/_jsx(TextField, _objectSpread({
            sx: {
              width: '100%'
            }
          }, params))]
        })
      }), /*#__PURE__*/_jsx(FormErrorMessage, {
        error: error
      })]
    });
  };
  DateField.propTypes = {
    field: PropTypes.shape({
      value: PropTypes.any,
      onChange: PropTypes.func
    }),
    fieldState: PropTypes.shape({
      error: PropTypes.any
    })
  };
  return DateField;
};

/**
 * This is a custom renderer for our Typeahead component to work with react-hook-form
 * @param {*} layout - Object containing the layout of the field
 * @param {string} layout.label - The label of the field
 * @param {boolean} layout.disabled - whether or not the field is disabled
 * @param {array} layout.choices - The choices for the field
 * @param {boolean} layout.required - whether or not the field is required
 * @param {string} layout.placeholder - The placeholder text for the field
 *
 * @returns {function} A custom renderer for the Typeahead component
 */
const typeaheadRenderer = _ref7 => {
  let {
    label,
    disabled,
    choices,
    required,
    placeholder
  } = _ref7;
  const WrappedTypeahead = _ref8 => {
    let {
      field,
      field: {
        onChange: _onChange
      },
      fieldState: {
        error
      }
    } = _ref8;
    // value is passed in via the react hook form inside of field
    // Ref is needed by the typeahead / autoComplete component and is passed in via props spreading
    return (
      /*#__PURE__*/
      // We need to manually connect a few props here for react hook form
      _jsx(Typeahead, _objectSpread(_objectSpread({}, field), {}, {
        sx: {
          width: '100%'
        },
        disabled: disabled,
        items: choices || [],
        label: label,
        isRequired: !!required
        // These are props that are passed to the MUI TextField rendered by Typeahead
        ,
        textFieldProps: {
          placeholder: placeholder || "Select ".concat(label),
          error: !!error,
          helperText: error === null || error === void 0 ? void 0 : error.message
        }
        // hooks-form appears to only want value and not the native onChange
        ,
        onChange: (_, newValue) => {
          _onChange((newValue === null || newValue === void 0 ? void 0 : newValue.id) || null);
        },
        error: error
      }))
    );
  };
  WrappedTypeahead.propTypes = {
    field: PropTypes.shape({
      value: PropTypes.any,
      onChange: PropTypes.func
    }),
    fieldState: PropTypes.shape({
      error: PropTypes.any
    })
  };
  return WrappedTypeahead;
};
const checkboxRenderer = layout => {
  const {
    label,
    disabled,
    choices = [],
    required,
    helperText
  } = layout;
  const Checkboxes = _ref9 => {
    let {
      field,
      fieldState: {
        error
      }
    } = _ref9;
    const [value, setValue] = useState(field.value || []);
    // FormControl expects error to be a boolean. If it's an object, it will throw an error
    return /*#__PURE__*/_jsx(_Fragment, {
      children: /*#__PURE__*/_jsxs(FormControl, {
        error: !!error,
        disabled: disabled,
        component: "fieldset",
        variant: "standard",
        children: [/*#__PURE__*/_jsxs(FormLabel, {
          component: "legend",
          children: [/*#__PURE__*/_jsx(RequiredIndicator, {
            isRequired: !!required
          }), label]
        }), helperText && /*#__PURE__*/_jsx(FormHelperText, {
          error: false,
          children: helperText
        }), /*#__PURE__*/_jsxs(FormGroup, {
          children: [choices.length === 0 && /*#__PURE__*/_jsx(FormHelperText, {
            children: "There are no options to select"
          }), choices === null || choices === void 0 ? void 0 : choices.map((item, index) => /*#__PURE__*/_jsx(FormControlLabel, {
            control: /*#__PURE__*/_jsx(Checkbox, {
              onBlur: field.onBlur,
              onChange: e => {
                let valueCopy = [...value];

                // update checkbox value
                valueCopy[index] = e.target.checked ? e.target.value : null;
                const noNulls = valueCopy.filter(v => v !== null && v !== undefined);

                // If the array ONLY contains null values, set it to an empty array for required validation purposes
                if (noNulls.length === 0) {
                  valueCopy = [];
                }
                // send data to react hook form
                field.onChange(valueCopy);

                // update local state
                setValue(valueCopy);
              }
            }),
            label: item.label,
            value: item.id
          }, item.id)), /*#__PURE__*/_jsx(FormErrorMessage, {
            error: error
          })]
        })]
      })
    });
  };
  Checkboxes.propTypes = {
    field: PropTypes.object,
    fieldState: PropTypes.object
  };
  return Checkboxes;
};
export default AnyField;